import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";

const FALLBACK_PILLARS = [
  {
    name: "Ustawienia Systemowe i Biologiczne",
    services: ["Ustawienia Hellingerowskie", "Konsultacja Totalnej Biologii"],
  },
  {
    name: "Transformacja Podświadomości i Energii",
    services: ["Sesja hipnozy", "Hipnoza regresyjna", "Dwupunkt / Matryca Energetyczna"],
  },
  {
    name: "Wgląd i Kierunek",
    services: ["Czytanie Kronik Akaszy"],
  },
];

const TIMES = ["09:00", "11:00", "13:00", "15:00", "17:00", "19:00"];

export default function BookingForm({ pillars }) {
  // Filary z tresci strony (props); normalizacja title->name + fallback gdy brak.
  const PILLARS = (pillars && pillars.length ? pillars : FALLBACK_PILLARS).map((p) => ({
    name: p.title || p.name,
    services: (p.services && p.services.length ? p.services : ["Konsultacja"]),
  }));

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    pillar: PILLARS[0].name,
    service: PILLARS[0].services[0],
    preferred_date: "",
    preferred_time: "11:00",
    session_format: "online",
    intention: "",
    current_state: "",
    previous_experience: "",
    agreement: false,
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onPillarChange = (p) => {
    set("pillar", p);
    const pillar = PILLARS.find((x) => x.name === p);
    set("service", pillar.services[0]);
  };

  const canNext1 = form.full_name && form.email && form.phone && form.preferred_date && form.preferred_time;
  const canSubmit = form.intention.length >= 10 && form.agreement;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Rezerwacja przyjęta. Odezwę się w ciągu 48 godzin.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Błąd rezerwacji");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="bg-white rounded-2xl p-10 md:p-14 border border-[#E5E0D8] text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#8A9A86]/10 mb-6">
          <Check size={28} className="text-[#8A9A86]" />
        </div>
        <h3 className="font-display text-3xl md:text-4xl mb-4">Dziękuję, że zaufałaś temu impulsowi.</h3>
        <p className="text-[#5C6670] max-w-lg mx-auto leading-relaxed">
          Otrzymałam Twoje zgłoszenie. Skontaktuję się osobiście w ciągu 48 godzin, by potwierdzić termin i przesłać szczegóły.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-8 md:p-12 lg:p-16 border border-[#E5E0D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="booking-form">
      <div className="flex items-center gap-4 mb-10">
        <div className="flex items-center gap-2">
          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${step >= 1 ? "bg-[#8A9A86] text-white" : "bg-[#E5E0D8] text-[#8A9A86]"}`}>1</span>
          <span className="text-xs uppercase tracking-widest text-[#5C6670]">Termin</span>
        </div>
        <div className="flex-1 h-px bg-[#E5E0D8]" />
        <div className="flex items-center gap-2">
          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${step >= 2 ? "bg-[#8A9A86] text-white" : "bg-[#E5E0D8] text-[#8A9A86]"}`}>2</span>
          <span className="text-xs uppercase tracking-widest text-[#5C6670]">Ankieta</span>
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-8" data-testid="booking-step-1">
          <div className="grid md:grid-cols-2 gap-8">
            <Field label="Imię i nazwisko">
              <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="booking-name" className={inputCls} />
            </Field>
            <Field label="Email">
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email" className={inputCls} />
            </Field>
            <Field label="Telefon">
              <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone" className={inputCls} />
            </Field>
            <Field label="Forma sesji">
              <select value={form.session_format} onChange={(e) => set("session_format", e.target.value)} data-testid="booking-format" className={inputCls}>
                <option value="online">Online (Zoom)</option>
              </select>
            </Field>
          </div>

          <Field label="Filar pracy">
            <div className="grid sm:grid-cols-3 gap-3" data-testid="booking-pillar-group">
              {PILLARS.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => onPillarChange(p.name)}
                  data-testid={`booking-pillar-${PILLARS.indexOf(p)}`}
                  className={`text-left p-4 rounded-xl border transition-all text-sm leading-snug ${
                    form.pillar === p.name
                      ? "border-[#8A9A86] bg-[#8A9A86]/5 text-[#2B2B2B]"
                      : "border-[#E5E0D8] text-[#5C6670] hover:border-[#8A9A86]/50"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Usługa">
            <select value={form.service} onChange={(e) => set("service", e.target.value)} data-testid="booking-service" className={inputCls}>
              {PILLARS.find((p) => p.name === form.pillar).services.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>

          <div className="grid md:grid-cols-2 gap-8">
            <Field label="Preferowana data">
              <input required type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="booking-date" className={inputCls} />
            </Field>
            <Field label="Preferowana godzina">
              <select value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} data-testid="booking-time" className={inputCls}>
                {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="button"
              disabled={!canNext1}
              onClick={() => setStep(2)}
              data-testid="booking-next-button"
              className="bg-[#2B2B2B] text-white rounded-full px-8 py-4 hover:bg-[#8A9A86] transition-colors disabled:opacity-40 inline-flex items-center gap-2"
            >
              Dalej — ankieta <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-8" data-testid="booking-step-2">
          <p className="text-[#5C6670] leading-relaxed">
            Krótka ankieta pomoże mi przygotować się do sesji. Odpowiedzi pozostają w pełni poufne.
          </p>

          <Field label="Z jaką intencją przychodzisz?">
            <textarea
              required minLength={10} rows={4}
              value={form.intention} onChange={(e) => set("intention", e.target.value)}
              data-testid="booking-intention"
              placeholder="Co chciałabyś przepracować / zobaczyć / uwolnić?"
              className={`${inputCls} resize-none`}
            />
          </Field>

          <Field label="Jak czujesz się obecnie? (opcjonalnie)">
            <textarea rows={3} value={form.current_state} onChange={(e) => set("current_state", e.target.value)} data-testid="booking-current-state" className={`${inputCls} resize-none`} />
          </Field>

          <Field label="Wcześniejsze doświadczenia z pracą tego typu (opcjonalnie)">
            <textarea rows={3} value={form.previous_experience} onChange={(e) => set("previous_experience", e.target.value)} data-testid="booking-experience" className={`${inputCls} resize-none`} />
          </Field>

          <label className="flex items-start gap-3 cursor-pointer text-sm text-[#5C6670] leading-relaxed">
            <input
              type="checkbox" checked={form.agreement} onChange={(e) => set("agreement", e.target.checked)}
              data-testid="booking-agreement" className="mt-1"
            />
            <span>Wyrażam zgodę na przetwarzanie moich danych w celu kontaktu i organizacji sesji.</span>
          </label>

          <div className="flex justify-between pt-4">
            <button
              type="button" onClick={() => setStep(1)} data-testid="booking-back-button"
              className="text-[#5C6670] hover:text-[#2B2B2B] transition-colors inline-flex items-center gap-2 text-sm"
            >
              <ArrowLeft size={16} /> Wróć
            </button>
            <button
              type="submit" disabled={!canSubmit || submitting} data-testid="booking-submit-button"
              className="bg-[#8A9A86] text-white rounded-full px-8 py-4 hover:bg-[#72826F] transition-colors disabled:opacity-40"
            >
              {submitting ? "Wysyłanie…" : "Zarezerwuj sesję"}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

const inputCls = "w-full bg-transparent border-b border-[#E5E0D8] py-3 focus:outline-none focus:border-[#8A9A86] transition-colors text-[#2B2B2B]";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-overline block mb-3">{label}</label>
      {children}
    </div>
  );
}
